import { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
  FileText,
  Download,
  Trash2,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  RefreshCw,
  Crown,
  Lock,
  Sparkles,
  FileBarChart,
  FileCheck,
  FileBadge,
  FileSearch,
  Eye,
  Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { supabase } from '../lib/supabaseClient';
import { useReports } from '../hooks/useReports';
import { formatDateTime, formatFileSize } from '../lib/utils';
import { toast } from 'sonner';

// Quick Report definitions for Standard tier
const QUICK_REPORTS = [
  {
    id: 'simple-emissions',
    name: 'Simple Emissions Summary',
    description: 'A concise overview of your Scope 1 & 2 emissions for the selected period.',
    icon: FileBarChart,
    color: 'bg-emerald-100 text-emerald-600',
    reportType: 'quick_emissions_summary'
  },
  {
    id: 'grant-compliance',
    name: 'Grant Compliance Report',
    description: 'Emissions data formatted for grant and funding applications.',
    icon: FileCheck,
    color: 'bg-blue-100 text-blue-600',
    reportType: 'quick_grant_compliance'
  },
  {
    id: 'tender-preparation',
    name: 'Tender Preparation Report',
    description: 'Summary suitable for tender submissions and procurement responses.',
    icon: FileBadge,
    color: 'bg-purple-100 text-purple-600',
    reportType: 'quick_tender_prep'
  },
  {
    id: 'emissions-evidence',
    name: 'Emissions Evidence Report',
    description: 'Detailed breakdown with supporting evidence for audits.',
    icon: FileSearch,
    color: 'bg-amber-100 text-amber-600',
    reportType: 'quick_emissions_evidence'
  }
];

// Sample reports for Demo tier
const SAMPLE_REPORTS = [
  {
    id: 'sample-1',
    name: 'Sample Emissions Report',
    description: 'Preview of a professional emissions report with watermark.',
    previewOnly: true
  },
  {
    id: 'sample-2',
    name: 'Sample Grant Report',
    description: 'Preview of a grant compliance report with watermark.',
    previewOnly: true
  }
];

export default function ReportsPage() {
  const navigate = useNavigate();
  const { currentOrg, subscriptionStatus, userData, canPerform, effectiveTier } = useOutletContext();
  const subscriptionTier = effectiveTier || 'demo';

  const [loading, setLoading] = useState(true);
  const [generatingReport, setGeneratingReport] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState('');

  const {
    reports,
    loading: reportsLoading,
    fetchReports,
    getDownloadUrl,
    deleteReport,
    requestReport,
    hasPendingReport
  } = useReports(currentOrg?.id, currentOrg?.name, userData);

  // Set loading state based on context availability
  useEffect(() => {
    if (currentOrg) {
      setLoading(false);
    }
  }, [currentOrg]);

  const isPro = subscriptionTier === 'pro';
  const isStandard = subscriptionTier === 'standard';
  const isDemo = subscriptionTier === 'demo';

  const handleDownload = async (report) => {
    if (!report.file_path && !report.file_url) {
      toast.error('Report file not available');
      return;
    }

    const url = await getDownloadUrl(report);
    if (url) {
      window.open(url, '_blank');
    } else {
      toast.error('Could not generate download link');
    }
  };

  const handleDelete = async (report) => {
    if (!window.confirm('Are you sure you want to delete this report?')) return;

    const { error } = await deleteReport(report.id, report.file_path);
    if (error) {
      toast.error(`Failed to delete: ${error}`);
    } else {
      toast.success('Report deleted');
    }
  };

  const handleQuickReport = async (quickReport) => {
    if (isDemo) {
      setUpgradeFeature('Quick Reports');
      setShowUpgradeModal(true);
      return;
    }

    setGeneratingReport(quickReport.id);

    // Get the current financial year from context or calculate it
    const now = new Date();
    const currentFY = now.getMonth() >= 6 ? now.getFullYear() + 1 : now.getFullYear();
    const selectedYear = outletContext?.selectedYear || currentFY;
    const fyYear = typeof selectedYear === 'number' ? selectedYear : currentFY;

    try {
      const result = await requestReport(
        fyYear,
        quickReport.reportType,
        null,
        false
      );

      if (result.success) {
        toast.success(`${quickReport.name} is being generated. We'll notify you when it's ready.`);
      } else {
        toast.error(result.error || 'Failed to generate report');
      }
    } catch (err) {
      toast.error('Failed to generate report');
    } finally {
      setGeneratingReport(null);
    }
  };

  const handleReportBuilder = () => {
    if (!isPro) {
      setUpgradeFeature('Report Builder');
      setShowUpgradeModal(true);
      return;
    }
    navigate('/report-builder');
  };

  const handleViewSample = () => {
    toast.info('Sample reports coming soon! Upgrade to Standard or Pro for full access.');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-emerald-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'processing':
        return <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />;
      default:
        return <Clock className="w-5 h-5 text-amber-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return 'Ready';
      case 'failed':
        return 'Failed';
      case 'processing':
        return 'Generating...';
      default:
        return 'Queued';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#003B5C]" />
      </div>
    );
  }

  return (
    <div className="bg-[#F8F9FA] min-h-[calc(100vh-56px)]">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-[#003B5C] flex items-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <FileBarChart className="w-7 h-7 mr-3 text-[#74A57F]" />
              Reports
            </h1>
            <p className="text-slate-600 mt-1">
              {currentOrg?.name} • Generate and download your emissions reports
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Pro users get Report Builder button */}
            {isPro ? (
              <Button
                onClick={() => navigate('/report-builder')}
                className="relative bg-gradient-to-r from-[#74A57F] to-[#003B5C] hover:opacity-90 animate-pulse-subtle"
                data-testid="report-builder-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Report Builder
                <span className="ml-2 text-[10px] bg-white/20 px-1.5 py-0.5 rounded font-semibold">BETA</span>
              </Button>
            ) : (
              <Button
                onClick={handleReportBuilder}
                variant="outline"
                className="relative border-purple-200 text-purple-700 hover:bg-purple-50 animate-pulse-subtle"
                data-testid="report-builder-btn-locked"
              >
                <Lock className="w-4 h-4 mr-2" />
                Report Builder
                <span className="ml-2 text-[10px] bg-purple-100 px-1.5 py-0.5 rounded font-semibold">BETA</span>
                <span className="ml-1 text-[10px] bg-purple-100 px-1.5 py-0.5 rounded font-semibold">PRO</span>
              </Button>
            )}
            <Button
              onClick={fetchReports}
              variant="outline"
              disabled={reportsLoading}
              data-testid="refresh-reports-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${reportsLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Subscription Tier Banner */}
        {isDemo && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6" data-testid="demo-banner">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Eye className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-amber-900">Demo Account - Preview Only</h3>
                <p className="text-sm text-amber-700 mt-1">
                  You can preview sample reports with watermarks. Upgrade to Standard for Quick Reports or Pro for full Report Builder access.
                </p>
                <Button
                  onClick={() => navigate('/pricing')}
                  size="sm"
                  className="mt-3 bg-amber-600 hover:bg-amber-700"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  View Plans
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Pending Report Notice */}
        {hasPendingReport && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex items-center space-x-3" data-testid="pending-report-notice">
            <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
            <div>
              <p className="font-medium text-blue-900">Report generating...</p>
              <p className="text-sm text-blue-700">We'll email you when it's ready. This usually takes 1-2 minutes.</p>
            </div>
          </div>
        )}

        {/* Quick Reports Section - Standard & Pro */}
        {(isStandard || isPro) && (
          <div className="mb-8" data-testid="quick-reports-section">
            <h2 className="text-lg font-semibold text-[#003B5C] mb-4">Quick Reports</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {QUICK_REPORTS.map((report) => {
                const Icon = report.icon;
                const isGenerating = generatingReport === report.id;

                return (
                  <button
                    key={report.id}
                    onClick={() => handleQuickReport(report)}
                    disabled={isGenerating || hasPendingReport}
                    className="bg-white rounded-xl p-4 border border-slate-200 hover:border-[#74A57F] hover:shadow-md transition-all text-left group disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid={`quick-report-${report.id}`}
                  >
                    <div className={`w-10 h-10 rounded-lg ${report.color} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                      {isGenerating ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Icon className="w-5 h-5" />
                      )}
                    </div>
                    <h3 className="font-medium text-slate-900 text-sm mb-1">{report.name}</h3>
                    <p className="text-xs text-slate-500 line-clamp-2">{report.description}</p>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Sample Reports Section - Demo Only */}
        {isDemo && (
          <div className="mb-8" data-testid="sample-reports-section">
            <h2 className="text-lg font-semibold text-[#003B5C] mb-4">Sample Reports</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {SAMPLE_REPORTS.map((report) => (
                <div
                  key={report.id}
                  className="bg-white rounded-xl p-4 border border-slate-200 relative overflow-hidden"
                  data-testid={`sample-report-${report.id}`}
                >
                  {/* Watermark overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-slate-50/50 to-amber-50/50 pointer-events-none" />
                  <div className="absolute top-2 right-2 bg-amber-100 text-amber-700 text-[10px] px-2 py-1 rounded font-semibold">
                    SAMPLE
                  </div>

                  <div className="relative">
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center mb-3">
                      <FileText className="w-5 h-5 text-slate-500" />
                    </div>
                    <h3 className="font-medium text-slate-900 text-sm mb-1">{report.name}</h3>
                    <p className="text-xs text-slate-500 mb-3">{report.description}</p>
                    <Button
                      onClick={handleViewSample}
                      variant="outline"
                      size="sm"
                      className="w-full"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Preview Sample
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pro Features Highlight - For Standard users */}
        {isStandard && (
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-6 mb-8" data-testid="pro-upsell">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 flex items-center justify-center flex-shrink-0">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-[#003B5C] mb-1">Unlock Report Builder</h3>
                <p className="text-sm text-slate-600 mb-3">
                  Upgrade to Pro for AI-assisted narrative generation, custom section selection, professional PDF & Word exports, and shareable verification URLs.
                </p>
                <Button
                  onClick={() => navigate('/pricing')}
                  className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:opacity-90"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Upgrade to Pro
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* My Reports Section */}
        <div data-testid="my-reports-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-[#003B5C]">My Reports</h2>
            <span className="text-sm text-slate-500">{reports.length} report{reports.length !== 1 ? 's' : ''}</span>
          </div>

          {reports.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-12 text-center" data-testid="no-reports">
              <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-semibold text-slate-700 mb-2">No Reports Yet</h3>
              <p className="text-slate-500 mb-6 max-w-md mx-auto">
                {isDemo
                  ? 'Upgrade to Standard to generate your first Quick Report, or Pro for full Report Builder access.'
                  : 'Generate your first emissions report using Quick Reports above or the Report Builder.'}
              </p>
              {isDemo && (
                <Button onClick={() => navigate('/pricing')}>
                  <Crown className="w-4 h-4 mr-2" />
                  View Plans
                </Button>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden overflow-x-auto">
              <table className="w-full min-w-[700px]">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Report
                    </th>
                    <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider w-24">
                      Status
                    </th>
                    <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider w-36">
                      Created
                    </th>
                    <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider w-20">
                      Size
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider w-28">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {reports.map((report) => (
                    <tr key={report.id} className="hover:bg-slate-50 transition-colors" data-testid={`report-row-${report.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-2">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${report.output_format === 'docx'
                            ? 'bg-blue-100'
                            : 'bg-[#003B5C]/10'
                            }`}>
                            <FileText className={`w-4 h-4 ${report.output_format === 'docx'
                              ? 'text-blue-600'
                              : 'text-[#003B5C]'
                              }`} />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <p className="font-medium text-slate-900 text-sm truncate max-w-[200px]" title={report.report_name}>
                                {report.report_name}
                              </p>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${report.output_format === 'docx'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-red-100 text-red-700'
                                }`}>
                                {report.output_format === 'docx' ? 'DOCX' : 'PDF'}
                              </span>
                            </div>
                            <p className="text-xs text-slate-500 truncate">{report.organisation_name}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center space-x-1">
                          {getStatusIcon(report.status)}
                          <span className={`text-xs font-medium ${report.status === 'completed' ? 'text-emerald-700' :
                            report.status === 'failed' ? 'text-red-700' :
                              report.status === 'processing' ? 'text-blue-700' :
                                'text-amber-700'
                            }`}>
                            {getStatusText(report.status)}
                          </span>
                        </div>
                        {report.error_message && (
                          <p className="text-[10px] text-red-500 mt-0.5 truncate">{report.error_message}</p>
                        )}
                      </td>
                      <td className="px-3 py-3 text-xs text-slate-600">
                        {formatDateTime(report.created_at)}
                      </td>
                      <td className="px-3 py-3 text-xs text-slate-600">
                        {report.file_size_bytes ? formatFileSize(report.file_size_bytes) : (report.status === 'completed' ? '—' : '-')}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end space-x-1">
                          {report.status === 'completed' && (
                            <Button
                              onClick={() => handleDownload(report)}
                              size="sm"
                              className="bg-[#74A57F] hover:bg-[#5F8575] text-xs px-2 py-1 h-7"
                              data-testid={`download-report-${report.id}`}
                            >
                              <Download className="w-3 h-3 mr-1" />
                              Download
                            </Button>
                          )}
                          <Button
                            onClick={() => handleDelete(report)}
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0"
                            data-testid={`delete-report-${report.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Info Footer */}
        <div className="mt-6 text-center text-sm text-slate-500">
          <p>Reports are stored securely. Download links expire after 7 days but can be regenerated anytime.</p>
        </div>
      </div>

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowUpgradeModal(false)}
          data-testid="upgrade-modal"
        >
          <div
            className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Crown className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-[#003B5C] mb-2">{upgradeFeature}</h3>
              <p className="text-slate-600 text-sm mb-4">
                {upgradeFeature === 'Report Builder'
                  ? 'Create professional, AI-assisted reports with custom sections, themes, and shareable verification URLs.'
                  : upgradeFeature === 'Quick Reports'
                    ? 'Generate instant emissions reports for grants, tenders, and compliance needs.'
                    : `Access ${upgradeFeature} and more with an upgraded plan.`
                }
              </p>
              <div className="bg-purple-50 rounded-lg p-3 mb-4">
                <p className="text-purple-700 text-sm font-medium">
                  {upgradeFeature === 'Report Builder' ? 'Available on Pro Plan' : 'Available on Standard & Pro Plans'}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowUpgradeModal(false)}
                >
                  Maybe Later
                </Button>
                <Button
                  className={`flex-1 ${!canPerform('manage_billing') ? 'bg-slate-400 cursor-not-allowed' : 'bg-gradient-to-r from-purple-600 to-indigo-600'}`}
                  onClick={() => {
                    if (!canPerform('manage_billing')) {
                      toast.error("You don't have permission to manage billing. Please contact your administrator.");
                      return;
                    }
                    setShowUpgradeModal(false);
                    navigate('/billing');
                  }}
                >
                  {upgradeFeature === 'Report Builder' ? 'Upgrade to Pro' : 'View Plans'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
